import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getOrgEvents, createEvent, rsvpEvent, deleteEvent, updateEvent, createFeedback, getOrgFeedbacks } from '../../api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import { StarRating, StarInput } from '../../components/StarRating';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import EmptyState from '../../components/EmptyState';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  ServerIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const COMMON_TIMEZONES = [
  'UTC',
  'Europe/Warsaw', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Europe/Amsterdam', 'Europe/Rome', 'Europe/Madrid', 'Europe/Helsinki',
  'Europe/Moscow', 'Europe/Istanbul',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Sao_Paulo', 'America/Argentina/Buenos_Aires',
  'Asia/Tokyo', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore',
  'Asia/Kolkata', 'Asia/Dubai',
  'Africa/Cairo', 'Africa/Lagos',
  'Australia/Sydney', 'Australia/Perth',
  'Pacific/Auckland', 'Pacific/Honolulu',
];

// Helper to get local date string YYYY-MM-DD without timezone shift
const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Get UTC offset in minutes for a timezone at a specific instant
function tzOffsetMinutes(date, tz) {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
    const p = {};
    for (const { type, value } of fmt.formatToParts(date)) p[type] = value;
    const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +(p.hour === '24' ? '0' : p.hour), +p.minute, +p.second);
    return (wall - date.getTime()) / 60000;
  } catch { return 0; }
}

// Get short timezone abbreviation (e.g. CET, EET)
function tzAbbr(date, tz) {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(date).find(p => p.type === 'timeZoneName')?.value || tz;
  } catch { return tz; }
}

// Convert event time from creator's timezone to viewer's timezone
const convertEventTime = (dateStr, timeStr, creatorTz) => {
  if (!dateStr || !timeStr) return { date: dateStr, time: timeStr, originalTime: timeStr, tzLabel: '', sameTz: true };
  const fromTz = creatorTz || 'UTC';
  try {
    const [y, mo, d] = dateStr.split('-').map(Number);
    const [h, mi] = timeStr.split(':').map(Number);
    // Guess: treat wall-clock as UTC, then find real UTC
    const guessUtc = new Date(Date.UTC(y, mo - 1, d, h, mi, 0));
    let offset = tzOffsetMinutes(guessUtc, fromTz);
    let actualUtc = new Date(guessUtc.getTime() - offset * 60000);
    // Re-check for DST boundary
    const offset2 = tzOffsetMinutes(actualUtc, fromTz);
    if (offset2 !== offset) actualUtc = new Date(guessUtc.getTime() - offset2 * 60000);
    // Format in viewer's timezone
    const vOff = tzOffsetMinutes(actualUtc, USER_TIMEZONE);
    const viewerMs = actualUtc.getTime() + vOff * 60000;
    const vd = new Date(viewerMs);
    const sameTz = fromTz === USER_TIMEZONE;
    const creatorAbbr = tzAbbr(actualUtc, fromTz);
    const viewerAbbr = tzAbbr(actualUtc, USER_TIMEZONE);
    return {
      date: `${vd.getUTCFullYear()}-${String(vd.getUTCMonth()+1).padStart(2,'0')}-${String(vd.getUTCDate()).padStart(2,'0')}`,
      time: `${String(vd.getUTCHours()).padStart(2,'0')}:${String(vd.getUTCMinutes()).padStart(2,'0')}`,
      originalTime: timeStr,
      originalTzAbbr: creatorAbbr,
      viewerTzAbbr: viewerAbbr,
      tzLabel: sameTz ? creatorAbbr : `${viewerAbbr} (${timeStr} ${creatorAbbr})`,
      sameTz
    };
  } catch {
    return { date: dateStr, time: timeStr, originalTime: timeStr, tzLabel: '', sameTz: true };
  }
};

export default function CalendarPage() {
  const { orgId } = useParams();
  const { user, organizations } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCreate, setShowCreate] = useState(false);
  const [showEvent, setShowEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [error, setError] = useState('');
  const [eventForm, setEventForm] = useState({ title: '', description: '', date: '', time: '', location: '', server: '', timezone: USER_TIMEZONE });
  const [showFeedback, setShowFeedback] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({ rating: 5, content: '', anonymous: false });
  const [success, setSuccess] = useState('');
  const [submittedFeedbackEventIds, setSubmittedFeedbackEventIds] = useState([]);
  const [showDayEvents, setShowDayEvents] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', description: '', date: '', time: '', location: '', server: '', timezone: USER_TIMEZONE });
  const [editShowTzPicker, setEditShowTzPicker] = useState(false);
  const [createShowTzPicker, setCreateShowTzPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const org = organizations.find(o => o._id === orgId);
  const canManageEvents = user?.role === 'admin' || org?.ownerId === user?._id || !!user?.permissions?.manageCalendar || !!user?.permissions?.manageEvents;
  const isOwner = canManageEvents;

  useEffect(() => { loadEvents(); loadFeedbacks(); }, [orgId]);

  const loadEvents = async () => {
    try {
      const res = await getOrgEvents(orgId);
      setEvents(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFeedbacks = async () => {
    try {
      const res = await getOrgFeedbacks(orgId);
      const myFeedbackEventIds = res.data.filter(f => f.userId === user._id).map(f => f.eventId);
      setSubmittedFeedbackEventIds(myFeedbackEventIds);
    } catch (err) {
      console.error(err);
    }
  };

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const days = [];

    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, otherMonth: true });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), otherMonth: false });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), otherMonth: true });
    }
    return days;
  }, [currentDate]);

  const getEventsForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return events.filter(e => e.date === dateStr);
  };

  const todayStr = toLocalDateStr(new Date());
  const upcomingEvents = events
    .filter(e => e.date >= todayStr)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 10);

  const pastEventsNeedingFeedback = events
    .filter(e => {
      return e.date < todayStr &&
        e.attending?.some(a => a.userId === user._id) &&
        !submittedFeedbackEventIds.includes(e._id);
    })
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 5);

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createEvent({ ...eventForm, organizationId: orgId });
      await loadEvents();
      setShowCreate(false);
      setCreateShowTzPicker(false);
      setEventForm({ title: '', description: '', date: '', time: '', location: '', server: '', timezone: USER_TIMEZONE });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create event');
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      const res = await rsvpEvent(eventId, status);
      setEvents(prev => prev.map(e => e._id === eventId ? res.data : e));
      if (showEvent?._id === eventId) setShowEvent(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to RSVP');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await deleteEvent(eventId);
      setEvents(prev => prev.filter(e => e._id !== eventId));
      setShowEvent(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete event');
    }
  };

  const openEdit = (event) => {
    // Pre-fill time converted to viewer's own timezone so they type in their local time
    const converted = event.time
      ? convertEventTime(event.date, event.time, event.timezone)
      : null;
    setEditForm({
      title: event.title || '',
      description: event.description || '',
      date: converted ? converted.date : (event.date || ''),
      time: converted ? converted.time : (event.time || ''),
      timezone: USER_TIMEZONE,
      location: event.location || '',
      server: event.server || '',
    });
    setEditShowTzPicker(false);
    setShowEdit(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!showEvent) return;
    setSaving(true);
    try {
      // editForm.timezone is the editor's chosen timezone — use it directly
      const res = await updateEvent(showEvent._id, { ...editForm });
      setEvents(prev => prev.map(ev => ev._id === showEvent._id ? res.data : ev));
      setShowEvent(res.data);
      setShowEdit(false);
      setEditShowTzPicker(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!showFeedback) return;
    try {
      await createFeedback({
        eventId: showFeedback._id,
        organizationId: orgId,
        rating: feedbackForm.rating,
        content: feedbackForm.content,
        anonymous: feedbackForm.anonymous
      });
      setSubmittedFeedbackEventIds(prev => [...prev, showFeedback._id]);
      setShowFeedback(null);
      setFeedbackForm({ rating: 5, content: '', anonymous: false });
      setSuccess('Feedback submitted! Thank you.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit feedback');
    }
  };

  const handleDayClick = (day) => {
    const dayEvents = getEventsForDate(day.date);
    if (dayEvents.length === 1) {
      setShowEvent(dayEvents[0]);
    } else if (dayEvents.length > 1) {
      setShowDayEvents({ date: day.date, events: dayEvents });
    } else if (canManageEvents) {
      setSelectedDate(day.date);
      setEventForm(prev => ({ ...prev, date: toLocalDateStr(day.date) }));
      setShowCreate(true);
    }
  };

  const isToday = (date) => {
    const t = new Date();
    return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
  };

  const getUserRSVP = (event) => {
    if (event.attending?.some(a => a.userId === user._id)) return 'attending';
    if (event.notAttending?.some(a => a.userId === user._id)) return 'not-attending';
    return null;
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Calendar</h2>
          <div className="subtitle">{org?.name || 'Organization'}</div>
        </div>
        {isOwner && (
          <button className="btn btn-primary" onClick={() => { setEventForm({ title: '', description: '', date: '', time: '', location: '', server: '', timezone: USER_TIMEZONE }); setCreateShowTzPicker(false); setShowCreate(true); }}>
            <PlusIcon className="icon" /> New Event
          </button>
        )}
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="calendar-layout">
        <div className="calendar-container">
          <div className="calendar-nav">
            <button className="btn btn-ghost" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}>
              <ChevronLeftIcon className="icon" />
            </button>
            <h3>{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
            <button className="btn btn-ghost" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}>
              <ChevronRightIcon className="icon" />
            </button>
          </div>

          <div className="calendar-grid">
            {DAYS.map(d => <div key={d} className="calendar-day-header">{d}</div>)}
            {calendarDays.map((day, i) => {
              const dayEvents = getEventsForDate(day.date);
              return (
                <div
                  key={i}
                  className={`calendar-day ${day.otherMonth ? 'other-month' : ''} ${isToday(day.date) ? 'today' : ''}`}
                  onClick={() => handleDayClick(day)}
                >
                  <span className="day-number">{day.date.getDate()}</span>
                  <div className="event-lines">
                    {dayEvents.slice(0, 3).map((ev, j) => (
                      <div key={j} className="event-line" title={ev.title}>{ev.title}</div>
                    ))}
                    {dayEvents.length > 3 && <div className="event-line-more">+{dayEvents.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="upcoming-panel">
            <h3>📅 Upcoming Events</h3>
            {upcomingEvents.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>
                No upcoming events
              </div>
            ) : (
              upcomingEvents.map(event => {
                const ct = convertEventTime(event.date, event.time, event.timezone);
                return (
                <div key={event._id} className="event-list-item" onClick={() => setShowEvent(event)}>
                  <div className="event-title">{event.title}</div>
                  <div className="event-date">
                    <CalendarIcon className="icon" />
                    {new Date(ct.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {event.time && <><ClockIcon className="icon" style={{ marginLeft: 6 }} /> {ct.time} <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{ct.sameTz ? ct.viewerTzAbbr : ct.tzLabel}</span></>}
                  </div>
                  <div className="event-rsvp">
                    <span className="rsvp-yes"><CheckCircleIcon className="icon" /> {event.attending?.length || 0}</span>
                    <span className="rsvp-no"><XCircleIcon className="icon" /> {event.notAttending?.length || 0}</span>
                  </div>
                </div>
                );
              })
            )}
          </div>

          {pastEventsNeedingFeedback.length > 0 && (
            <div className="upcoming-panel">
              <h3>💬 Leave Feedback</h3>
              {pastEventsNeedingFeedback.map(event => (
                <div key={event._id} className="event-list-item" onClick={() => { setShowFeedback(event); setFeedbackForm({ rating: 5, content: '', anonymous: false }); }}>
                  <div className="event-title">{event.title}</div>
                  <div className="event-date">
                    <CalendarIcon className="icon" />
                    {new Date(event.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <span className="badge badge-warning" style={{ marginTop: 4, display: 'inline-flex' }}>
                    <DocumentTextIcon className="icon" /> Leave feedback
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Event Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create Event"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreateEvent}>Create Event</button>
          </>
        }
      >
        <form onSubmit={handleCreateEvent}>
          <div className="form-group">
            <label>Event Title</label>
            <input className="form-input" value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} placeholder="e.g. Weekly Trail Ride" required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-input" value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} placeholder="Event details..." rows={3} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Date</label>
              <input className="form-input" type="date" value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Time</label>
              <input className="form-input" type="time" value={eventForm.time} onChange={e => setEventForm({...eventForm, time: e.target.value})} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '-4px 0 12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Timezone:</span>
            {createShowTzPicker ? (
              <>
                <select
                  className="form-input"
                  style={{ padding: '3px 8px', fontSize: 12, height: 'auto', width: 'auto' }}
                  value={eventForm.timezone || USER_TIMEZONE}
                  onChange={e => setEventForm(f => ({ ...f, timezone: e.target.value }))}
                >
                  {!COMMON_TIMEZONES.includes(eventForm.timezone) && eventForm.timezone && (
                    <option value={eventForm.timezone}>{eventForm.timezone.replace(/_/g, ' ')}</option>
                  )}
                  {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
                </select>
                <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setCreateShowTzPicker(false)}>Done</button>
              </>
            ) : (
              <>
                <span style={{ fontSize: 12, fontWeight: 500 }}>{(eventForm.timezone || USER_TIMEZONE).replace(/_/g, ' ')}</span>
                <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setCreateShowTzPicker(true)}>Change</button>
              </>
            )}
          </div>
          <div className="form-group">
            <label>Location (in-game)</label>
            <input className="form-input" value={eventForm.location} onChange={e => setEventForm({...eventForm, location: e.target.value})} placeholder="e.g. Moorland Stables" />
          </div>
          <div className="form-group">
            <label>Server</label>
            <input className="form-input" value={eventForm.server} onChange={e => setEventForm({...eventForm, server: e.target.value})} placeholder="e.g. Star Glow" />
          </div>
        </form>
      </Modal>

      {/* Event Detail Modal */}
      <Modal isOpen={!!showEvent} onClose={() => setShowEvent(null)} title={showEvent?.title || ''} large
        footer={isOwner && showEvent ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(showEvent)}>
              <PencilIcon className="icon" /> Edit
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteEvent(showEvent._id)}>
              <TrashIcon className="icon" /> Delete Event
            </button>
          </div>
        ) : null}
      >
        {showEvent && (() => {
          const converted = convertEventTime(showEvent.date, showEvent.time, showEvent.timezone);
          return (
          <>
            {showEvent.description && (
              <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14, lineHeight: 1.6 }}>
                {showEvent.description}
              </p>
            )}
            <div className="event-detail-grid">
              <div className="event-detail-item">
                <span className="detail-icon"><CalendarIcon className="icon" /></span>
                <div>
                  <div className="detail-label">Date</div>
                  {new Date(converted.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
              {showEvent.time && (
                <div className="event-detail-item">
                  <span className="detail-icon"><ClockIcon className="icon" /></span>
                  <div>
                    <div className="detail-label">Time</div>
                    {converted.time}
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                      {converted.sameTz ? converted.viewerTzAbbr : converted.tzLabel}
                    </span>
                  </div>
                </div>
              )}
              {showEvent.location && (
                <div className="event-detail-item">
                  <span className="detail-icon"><MapPinIcon className="icon" /></span>
                  <div><div className="detail-label">Location</div>{showEvent.location}</div>
                </div>
              )}
              {showEvent.server && (
                <div className="event-detail-item">
                  <span className="detail-icon"><ServerIcon className="icon" /></span>
                  <div><div className="detail-label">Server</div>{showEvent.server}</div>
                </div>
              )}
            </div>

            <div className="rsvp-section">
              <h4>Attendance</h4>
              <div className="rsvp-buttons">
                <button
                  className={`btn btn-sm ${getUserRSVP(showEvent) === 'attending' ? 'btn-success' : 'btn-secondary'}`}
                  onClick={() => handleRSVP(showEvent._id, 'attending')}
                >
                  <CheckCircleIcon className="icon" /> I'll be there
                </button>
                <button
                  className={`btn btn-sm ${getUserRSVP(showEvent) === 'not-attending' ? 'btn-danger' : 'btn-secondary'}`}
                  onClick={() => handleRSVP(showEvent._id, 'not-attending')}
                >
                  <XCircleIcon className="icon" /> Can't make it
                </button>
              </div>
              <div className="rsvp-count">
                <span style={{ color: 'var(--success)' }}><CheckCircleIcon className="icon" /> {showEvent.attending?.length || 0} attending</span>
                <span style={{ color: 'var(--danger)' }}><XCircleIcon className="icon" /> {showEvent.notAttending?.length || 0} not attending</span>
              </div>
              {showEvent.attending?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Attending:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {showEvent.attending.map((a, i) => <span key={i} className="badge badge-success">{a.displayName}</span>)}
                  </div>
                </div>
              )}
              {showEvent.notAttending?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Not attending:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {showEvent.notAttending.map((a, i) => <span key={i} className="badge badge-danger">{a.displayName}</span>)}
                  </div>
                </div>
              )}
            </div>
          </>
          );
        })()}
      </Modal>

      {/* Edit Event Modal */}
      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} title="Edit Event"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </>
        }
      >
        <form onSubmit={handleSaveEdit}>
          <div className="form-group">
            <label>Title</label>
            <input className="form-input" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-input" value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Date</label>
              <input className="form-input" type="date" value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>Time</label>
              <input className="form-input" type="time" value={editForm.time} onChange={e => setEditForm(f => ({ ...f, time: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '-4px 0 12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Timezone:</span>
            {editShowTzPicker ? (
              <>
                <select
                  className="form-input"
                  style={{ padding: '3px 8px', fontSize: 12, height: 'auto', width: 'auto' }}
                  value={editForm.timezone || USER_TIMEZONE}
                  onChange={e => setEditForm(f => ({ ...f, timezone: e.target.value }))}
                >
                  {!COMMON_TIMEZONES.includes(editForm.timezone) && editForm.timezone && (
                    <option value={editForm.timezone}>{editForm.timezone.replace(/_/g, ' ')}</option>
                  )}
                  {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
                </select>
                <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setEditShowTzPicker(false)}>Done</button>
              </>
            ) : (
              <>
                <span style={{ fontSize: 12, fontWeight: 500 }}>{(editForm.timezone || USER_TIMEZONE).replace(/_/g, ' ')}</span>
                <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setEditShowTzPicker(true)}>Change</button>
                {(editForm.timezone || USER_TIMEZONE) !== USER_TIMEZONE && (
                  <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '2px 8px', color: 'var(--text-muted)' }} onClick={() => setEditForm(f => ({ ...f, timezone: USER_TIMEZONE }))}>Reset to mine</button>
                )}
              </>
            )}
          </div>
          <div className="form-group">
            <label>Location (in-game)</label>
            <input className="form-input" value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Moorland Stables" />
          </div>
          <div className="form-group">
            <label>Server</label>
            <input className="form-input" value={editForm.server} onChange={e => setEditForm(f => ({ ...f, server: e.target.value }))} placeholder="e.g. Star Glow" />
          </div>
        </form>
      </Modal>

      {/* Day Events Picker Modal */}
      <Modal isOpen={!!showDayEvents} onClose={() => setShowDayEvents(null)} title="Choose event">
        {showDayEvents && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {showDayEvents.events.map(ev => {
              const ct = convertEventTime(ev.date, ev.time, ev.timezone);
              return (
                <div
                  key={ev._id}
                  onClick={() => { setShowDayEvents(null); setShowEvent(ev); }}
                  className="event-list-item"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="event-title">{ev.title}</div>
                  <div className="event-date">
                    {ev.time && <><ClockIcon className="icon" /> {ct.time}</>}
                    {ev.location && <><MapPinIcon className="icon" style={{ marginLeft: 6 }} /> {ev.location}</>}
                  </div>
                  <div className="event-rsvp">
                    <span className="rsvp-yes"><CheckCircleIcon className="icon" /> {ev.attending?.length || 0}</span>
                    <span className="rsvp-no"><XCircleIcon className="icon" /> {ev.notAttending?.length || 0}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* Feedback Modal */}
      <Modal isOpen={!!showFeedback} onClose={() => setShowFeedback(null)} title={`Feedback: ${showFeedback?.title || ''}`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowFeedback(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmitFeedback}>Submit Feedback</button>
          </>
        }
      >
        {showFeedback && (
          <form onSubmit={handleSubmitFeedback}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              <CalendarIcon className="icon" style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {new Date(showFeedback.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="form-group">
              <label>Rating</label>
              <StarInput value={feedbackForm.rating} onChange={r => setFeedbackForm(prev => ({ ...prev, rating: r }))} />
            </div>
            <div className="form-group">
              <label>Your Feedback</label>
              <textarea
                className="form-input"
                value={feedbackForm.content}
                onChange={e => setFeedbackForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Share your thoughts about this event..."
                rows={4}
                required
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={feedbackForm.anonymous}
                onChange={e => setFeedbackForm(prev => ({ ...prev, anonymous: e.target.checked }))}
              />
              Submit anonymously
            </label>
          </form>
        )}
      </Modal>
    </div>
  );
}
